import type { Socket } from "socket.io-client"
import { create } from "zustand"
import { persist } from "zustand/middleware"

import {
	createLiveChatSocket,
	subscribeToSession,
	unsubscribeFromSession as unsubscribeSocket,
} from "../sockets/live-chat"

interface LiveChatState {
	liveChatSocket: Socket | null
	isLiveChatConnected: boolean
	isLiveChatOpen: boolean
	currentSessionId: string | null
	setIsLiveChatOpen: (open: boolean) => void
	connectToLiveChat: (
		sessionId: string,
		userData: { name?: string; imageUrl?: string; userId?: string },
	) => Socket | null
	unsubscribeFromSession: () => void
	disconnectFromLiveChat: () => void
}

export const useLiveChatStore = create<LiveChatState>()(
	persist(
		(set, get) => ({
			liveChatSocket: null,
			isLiveChatConnected: false,
			isLiveChatOpen: false,
			currentSessionId: null,
			setIsLiveChatOpen: (open) => set({ isLiveChatOpen: open }),
			connectToLiveChat: (
				sessionId: string,
				userData: { name?: string; imageUrl?: string; userId?: string },
			) => {
				const { liveChatSocket, currentSessionId } = get()

				// Already connected to this session — no-op
				if (liveChatSocket?.connected && currentSessionId === sessionId) {
					return liveChatSocket
				}

				// Connected but to a different session — switch session, reuse socket
				if (liveChatSocket?.connected && currentSessionId !== sessionId) {
					subscribeToSession(liveChatSocket, sessionId, userData)
					set({ currentSessionId: sessionId })
					return liveChatSocket
				}

				// Socket exists but disconnected - clean it up
				if (liveChatSocket) {
					const teardown = (
						liveChatSocket as Socket & { disconnectFn?: () => void }
					).disconnectFn
					if (teardown) teardown()
					else liveChatSocket.disconnect()
				}

				// Create new socket
				try {
					const { socket, disconnect } = createLiveChatSocket({
						onConnect: () => {
							set({ isLiveChatConnected: true })
						},
						onDisconnect: () => {
							set({ isLiveChatConnected: false })
						},
						onReconnect: () => {
							set({ isLiveChatConnected: true })
						},
						onSessionDeleted: () => {
							set({
								liveChatSocket: null,
								isLiveChatConnected: false,
								isLiveChatOpen: false,
								currentSessionId: null,
							})
						},
					})

					// Store disconnect function on socket for cleanup
					;(socket as Socket & { disconnectFn?: () => void }).disconnectFn =
						disconnect

					// Subscribe to the session
					subscribeToSession(socket, sessionId, userData)

					set({
						liveChatSocket: socket,
						currentSessionId: sessionId,
					})

					return socket
				} catch (error) {
					console.error("Error connecting to live chat:", error)
					return null
				}
			},
			unsubscribeFromSession: () => {
				const { liveChatSocket } = get()
				if (liveChatSocket) {
					unsubscribeSocket(liveChatSocket)
				}
				set({ currentSessionId: null, isLiveChatOpen: false })
			},
			disconnectFromLiveChat: () => {
				const { liveChatSocket } = get()
				if (liveChatSocket) {
					if (
						(liveChatSocket as Socket & { disconnectFn?: () => void })
							.disconnectFn
					) {
						;(liveChatSocket as Socket & { disconnectFn?: () => void })
							.disconnectFn!()
					} else {
						liveChatSocket.off("connect")
						liveChatSocket.off("disconnect")
						liveChatSocket.off("reconnect")
						liveChatSocket.off("live-chat:message")
						liveChatSocket.off("live-chat:user-joined")
						liveChatSocket.off("live-chat:user-left")
						liveChatSocket.off("live-chat:session-deleted")
						liveChatSocket.disconnect()
					}

					set({
						liveChatSocket: null,
						isLiveChatConnected: false,
						isLiveChatOpen: false,
						currentSessionId: null,
					})
				}
			},
		}),
		{
			name: "live-chat-state",
			partialize: (state) => ({
				isLiveChatConnected: state.isLiveChatConnected,
				isLiveChatOpen: state.isLiveChatOpen,
				// Exclude liveChatSocket and currentSessionId from persistence
			}),
		},
	),
)
